import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Upload, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPublicationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    categories: [],
    description: '',
    restrictions: '',
    sample_links: [''],
    language: 'English',
    da: '',
    image_resolution: '',
    disclaimer: false,
    backlink: true,
    is_featured: false,
    tagline: '',
    is_active: true
  });

  const CATEGORIES = [
    'Business',
    'Startup',
    'Tech',
    'Real Estate',
    'Healthcare',
    'Lifestyle',
    'Entertainment',
    'Finance',
    'Education',
    'Sports'
  ];

  useEffect(() => {
    if (isEdit) {
      fetchPublication();
    }
  }, [id]);

  const fetchPublication = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(`${API}/admin/publications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      // Handle migration from old 'category' to new 'categories' array
      if (!data.categories && data.category) {
        data.categories = [data.category];
      }
      setFormData(data);
    } catch (error) {
      console.error('Error fetching publication:', error);
      toast.error('Failed to load publication');
      navigate('/admin/publications');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      const response = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({ ...prev, logo: BACKEND_URL + response.data.url }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleAddSampleLink = () => {
    setFormData(prev => ({
      ...prev,
      sample_links: [...prev.sample_links, '']
    }));
  };

  const handleRemoveSampleLink = (index) => {
    setFormData(prev => ({
      ...prev,
      sample_links: prev.sample_links.filter((_, i) => i !== index)
    }));
  };

  const handleSampleLinkChange = (index, value) => {
    setFormData(prev => {
      const newLinks = [...prev.sample_links];
      newLinks[index] = value;
      return { ...prev, sample_links: newLinks };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.categories?.length || !formData.description) {
      toast.error('Please fill all required fields (including at least one category)');
      return;
    }

    const token = localStorage.getItem('token');
    const submitData = {
      ...formData,
      da: formData.da ? parseInt(formData.da) : null,
      sample_links: formData.sample_links.filter(link => link.trim() !== '')
    };
    // Remove old category field if present
    delete submitData.category;

    try {
      setLoading(true);
      
      if (isEdit) {
        await axios.put(`${API}/admin/publications/${id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Publication updated successfully');
      } else {
        await axios.post(`${API}/admin/publications`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Publication created successfully');
      }
      
      navigate('/admin/publications');
    } catch (error) {
      console.error('Error saving publication:', error);
      toast.error(error.response?.data?.detail || 'Failed to save publication');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="font-playfair text-4xl font-bold mb-2">
          {isEdit ? 'Edit Publication' : 'Create Publication'}
        </h1>
        <p className="font-manrope text-sm text-gray-600">Manage media outlet information</p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl">
        <div className="border border-gray-200 p-6 mb-6 bg-white">
          <h2 className="font-playfair text-xl font-bold mb-4">Basic Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Outlet Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="publication-name-input"
                required
                className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              />
            </div>

            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Logo</label>
              <div className="flex items-start gap-4">
                {formData.logo && (
                  <div className="w-32 h-32 border border-gray-200 p-2">
                    <img src={formData.logo} alt="Logo preview" className="w-full h-full object-contain" />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    data-testid="logo-upload-input"
                    className="hidden"
                    id="logo-upload"
                  />
                  <label
                    htmlFor="logo-upload"
                    className="inline-flex items-center gap-2 px-6 py-3 border border-gray-200 font-manrope text-xs uppercase tracking-widest hover:border-black cursor-pointer transition-all duration-300"
                  >
                    <Upload className="w-4 h-4" />
                    {uploading ? 'Uploading...' : 'Upload Logo'}
                  </label>
                  <p className="font-manrope text-xs text-gray-500 mt-2">Recommended: 150x50px, PNG or JPG</p>
                </div>
              </div>
            </div>

            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Categories * (Select multiple)</label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {CATEGORIES.map(cat => (
                  <label 
                    key={cat}
                    className={`flex items-center gap-2 p-3 border cursor-pointer transition-all ${
                      formData.categories?.includes(cat) 
                        ? 'border-black bg-black text-white' 
                        : 'border-gray-200 hover:border-black'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.categories?.includes(cat) || false}
                      onChange={(e) => {
                        const newCategories = e.target.checked
                          ? [...(formData.categories || []), cat]
                          : (formData.categories || []).filter(c => c !== cat);
                        setFormData({ ...formData, categories: newCategories });
                      }}
                      className="sr-only"
                    />
                    <span className="font-manrope text-xs uppercase tracking-widest">{cat}</span>
                  </label>
                ))}
              </div>
              {formData.categories?.length === 0 && (
                <p className="font-manrope text-xs text-red-500 mt-2">Please select at least one category</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Language *</label>
                <select
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  data-testid="language-select"
                  className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                >
                  <option value="English">English</option>
                  <option value="Hindi">Hindi</option>
                  <option value="Tamil">Tamil</option>
                  <option value="Telugu">Telugu</option>
                  <option value="Kannada">Kannada</option>
                  <option value="Malayalam">Malayalam</option>
                  <option value="Marathi">Marathi</option>
                  <option value="Bengali">Bengali</option>
                  <option value="Gujarati">Gujarati</option>
                  <option value="Punjabi">Punjabi</option>
                  <option value="Multi-language">Multi-language</option>
                </select>
              </div>

              <div>
                <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">DA (Domain Authority)</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.da}
                  onChange={(e) => setFormData({ ...formData, da: e.target.value ? parseInt(e.target.value) : '' })}
                  placeholder="1-100"
                  data-testid="da-input"
                  className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                />
                <p className="font-manrope text-xs text-gray-500 mt-1">Domain Authority score (1-100)</p>
              </div>
            </div>

            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Image Resolution</label>
              <input
                type="text"
                value={formData.image_resolution}
                onChange={(e) => setFormData({ ...formData, image_resolution: e.target.value })}
                placeholder="e.g., 1200x630, HD, 4K"
                data-testid="image-resolution-input"
                className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              />
              <p className="font-manrope text-xs text-gray-500 mt-1">Required image resolution for articles</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex items-center gap-3 cursor-pointer border border-gray-200 p-4 hover:border-black transition-all">
                <input
                  type="checkbox"
                  checked={formData.disclaimer}
                  onChange={(e) => setFormData({ ...formData, disclaimer: e.target.checked })}
                  data-testid="disclaimer-checkbox"
                  className="w-4 h-4"
                />
                <div>
                  <div className="font-manrope text-sm font-semibold">Disclaimer</div>
                  <div className="font-manrope text-xs text-gray-600">Article includes sponsored/promotional disclaimer</div>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer border border-gray-200 p-4 hover:border-black transition-all">
                <input
                  type="checkbox"
                  checked={formData.backlink}
                  onChange={(e) => setFormData({ ...formData, backlink: e.target.checked })}
                  data-testid="backlink-checkbox"
                  className="w-4 h-4"
                />
                <div>
                  <div className="font-manrope text-sm font-semibold">Backlink</div>
                  <div className="font-manrope text-xs text-gray-600">Dofollow/Nofollow links permitted</div>
                </div>
              </label>
            </div>

            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                data-testid="description-textarea"
                required
                rows="3"
                className="w-full border border-gray-200 px-4 py-3 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              />
            </div>

            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Restrictions / Content Guidelines *</label>
              <textarea
                value={formData.restrictions}
                onChange={(e) => setFormData({ ...formData, restrictions: e.target.value })}
                data-testid="restrictions-textarea"
                required
                rows="3"
                placeholder="E.g., No direct product promotions. Focus on newsworthy business stories..."
                className="w-full border border-gray-200 px-4 py-3 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              />
            </div>
          </div>
        </div>

        <div className="border border-gray-200 p-6 mb-6 bg-white">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-playfair text-xl font-bold">Sample Links</h2>
            <button
              type="button"
              onClick={handleAddSampleLink}
              data-testid="add-sample-link-button"
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 font-manrope text-xs uppercase tracking-widest hover:border-black transition-all duration-300"
            >
              <Plus className="w-4 h-4" />
              Add Link
            </button>
          </div>
          
          <div className="space-y-3">
            {formData.sample_links.map((link, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="url"
                  value={link}
                  onChange={(e) => handleSampleLinkChange(index, e.target.value)}
                  placeholder="https://example.com/article"
                  data-testid={`sample-link-input-${index}`}
                  className="flex-1 border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                />
                {formData.sample_links.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveSampleLink(index)}
                    data-testid={`remove-sample-link-${index}`}
                    className="p-2 border border-gray-200 hover:border-red-600 hover:text-red-600 transition-all duration-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="border border-gray-200 p-6 mb-6 bg-white">
          <h2 className="font-playfair text-xl font-bold mb-4">Homepage & Visibility Settings</h2>
          
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer border border-gray-200 p-4 hover:border-black transition-all">
              <input
                type="checkbox"
                checked={formData.is_featured}
                onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                data-testid="is-featured-checkbox"
                className="w-4 h-4"
              />
              <div>
                <div className="font-manrope text-sm font-semibold">Featured Publication</div>
                <div className="font-manrope text-xs text-gray-600">Show this publication prominently on homepage</div>
              </div>
            </label>

            {formData.is_featured && (
              <div>
                <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Tagline (for featured display)</label>
                <input
                  type="text"
                  value={formData.tagline || ''}
                  onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                  placeholder="e.g., India's #1 Business Daily, 8M+ daily readers"
                  data-testid="tagline-input"
                  className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                />
                <p className="font-manrope text-xs text-gray-500 mt-1">Short description shown when featured on homepage</p>
              </div>
            )}

            <label className="flex items-center gap-3 cursor-pointer border border-gray-200 p-4 hover:border-black transition-all">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                data-testid="is-active-checkbox"
                className="w-4 h-4"
              />
              <div>
                <div className="font-manrope text-sm font-semibold">Active</div>
                <div className="font-manrope text-xs text-gray-600">Show this publication in marketplace</div>
              </div>
            </label>
          </div>
        </div>

        <div className="flex gap-4">
          <button
            type="submit"
            disabled={loading}
            data-testid="submit-publication-button"
            className="px-8 py-3 bg-black text-white font-manrope text-xs uppercase tracking-widest hover:bg-gray-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Publication' : 'Create Publication')}
          </button>
          
          <button
            type="button"
            onClick={() => navigate('/admin/publications')}
            data-testid="cancel-button"
            className="px-8 py-3 border border-gray-200 font-manrope text-xs uppercase tracking-widest hover:border-black transition-all duration-300"
          >
            Cancel
          </button>
        </div>
      </form>
    </AdminLayout>
  );
};

export default AdminPublicationForm;